import { Container, Grid, Button, Typography, Accordion, AccordionActions, AccordionDetails, AccordionSummary, Dialog, DialogActions, DialogTitle, TextField, FormControl, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { React, useEffect, useState } from "react";
import { useAuth } from '../context/AuthContext';
import globals from "../globals";

function meetingStatusLabel(status) {
    if (status === 'upcoming') return 'Предстояща';
    if (status === 'passed') return 'Минала';
    return status;
}

function MeetingStatusText({ status, notStarted, recordingDuration }) {
    const isUpcoming = status === 'upcoming';
    const isPassed = status === 'passed';
    return (
        <Typography
            component="span"
            sx={{
                color: isUpcoming
                    ? globals.colors.lightGreen
                    : isPassed
                        ? globals.colors.lightRed
                        : 'inherit',
                fontWeight: isUpcoming || isPassed ? 'bold' : 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
            }}
        >
            {meetingStatusLabel(status)}
            {notStarted && (
                <CloseIcon
                    fontSize="small"
                    titleAccess="Не е започната"
                    sx={{ color: globals.colors.lightRed }}
                />
            )}
            {!notStarted && recordingDuration && (
                <Typography component="span" sx={{ fontWeight: 'normal', color: globals.colors.gray }}>
                    {recordingDuration}
                </Typography>
            )}
        </Typography>
    );
}

const ZoomPage = () => {

    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const { token } = useAuth();
    const [meetings, setMeetings] = useState([]);
    const [totalMeetings, setTotalMeetings] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [refreshIndex, setRefreshIndex] = useState(0);

    const handleClickOpenDialog = (meetingId) => {
        setDeleteTargetId(meetingId);
    };

    const handleCloseDialog = () => {
        setDeleteTargetId(null);
    };

    const handleConfirmDelete = () => {
        if (!deleteTargetId) return;
        setDeleting(true);
        fetch(`${globals.apiUrl}/admin/zoom-meetings/${deleteTargetId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        })
            .then(() => {
                setMeetings((prev) => prev.filter((meeting) => meeting.id !== deleteTargetId));
                setRefreshIndex((prev) => prev + 1);
            })
            .catch(console.error)
            .finally(() => {
                setDeleting(false);
                setDeleteTargetId(null);
            });
    };

    useEffect(() => {
        if (!token) return;
        const params = new URLSearchParams({
            status: statusFilter,
        });
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);
        fetch(`${globals.apiUrl}/admin/zoom-meetings?${params}`, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        })
            .then((res) => res.json())
            .then((data) => {
                setMeetings(Array.isArray(data?.meetings) ? data.meetings : []);
                setTotalMeetings(typeof data?.total === 'number' ? data.total : null);
            })
            .catch(console.error);
    }, [token, startDate, endDate, statusFilter, refreshIndex]);

    return (
        <Container>
            <Grid container spacing={10}>
                <Grid item sm={6}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 25, textTransform: 'uppercase' }}>
                        Zoom срещи
                    </Typography>
                </Grid>
                <Grid item sm={6}>
                    <Button startIcon={<AddIcon />} variant="contained" color="secondary" href="/admin-add-zoom">Добави среща</Button>
                </Grid>
            </Grid>

            <Grid container sx={{ mt: 3 }} spacing={5} alignItems="center">
                <Grid item>
                    <TextField
                        type="date"
                        label="Начало"
                        value={startDate}
                        onChange={(e) => {
                            setStartDate(e.target.value);
                            setStatusFilter('all');
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item>
                    -
                </Grid>
                <Grid item>
                    <TextField
                        type="date"
                        label="Край"
                        value={endDate}
                        onChange={(e) => {
                            setEndDate(e.target.value);
                            setStatusFilter('all');
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            </Grid>

            <Grid sx={{ mt: 1 }}>
                <FormControl>
                    <RadioGroup
                        row
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value)}
                    >
                        <FormControlLabel
                            value="all"
                            control={<Radio />}
                            label={totalMeetings != null ? `Всички (${totalMeetings})` : 'Всички'}
                        />
                        <FormControlLabel value="upcoming" control={<Radio />} label="Предстоящи" />
                        <FormControlLabel value="passed" control={<Radio />} label="Минали" />
                    </RadioGroup>
                </FormControl>
            </Grid>

            <Grid container sx={{ mt: 2 }} spacing={1}>
                {meetings.length === 0 && (
                    <Grid item sm={12}>
                        <Typography>Няма срещи за избрания период.</Typography>
                    </Grid>
                )}
                {meetings.map(meeting => (
                    <Grid item sm={12} key={meeting.id}>
                        <Accordion fullWidth>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography component="span">
                                    <strong>{meeting.time} / {meeting.date}</strong>
                                    {' / '}
                                    <Typography component="span" sx={{ color: globals.colors.darkBlue, fontWeight: 'bold' }}>
                                        {meeting.course}
                                    </Typography>
                                    {' / '}
                                    <MeetingStatusText status={meeting.status} notStarted={meeting.not_started} recordingDuration={meeting.recording_duration} />
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    <strong>Тема:</strong> {meeting.topic}
                                </Typography>
                                <Typography><strong>Учител:</strong> {meeting.instructor}</Typography>
                                <Typography><strong>Ученик:</strong> {meeting.student}</Typography>
                                <Typography><strong>Статус:</strong> <MeetingStatusText status={meeting.status} notStarted={meeting.not_started} recordingDuration={meeting.recording_duration} /></Typography>
                                <Typography><strong>Тип на среща:</strong> {meeting.type === 'normal' ? "Регулярна" : "Безплатна"}</Typography>
                            </AccordionDetails>
                            <AccordionActions>
                                <Button variant="contained" href={meeting.zoom_link} target="_blank" rel="noopener noreferrer" color='success'>Пусни срещата</Button>
                                <Button variant='contained' color='secondary' href={`/admin-edit-zoom/${meeting.id}`}>Редактирай</Button>
                                <Button onClick={() => handleClickOpenDialog(meeting.id)} variant='contained' color='error'>Изтрий</Button>
                                <Dialog
                                    open={deleteTargetId === meeting.id}
                                    onClose={handleCloseDialog}
                                >
                                    <DialogTitle id="alert-dialog-title">
                                        {"Искате ли да изтриете тази среща?"}
                                    </DialogTitle>
                                    <DialogActions>
                                        <Button onClick={handleConfirmDelete} disabled={deleting} autoFocus>
                                            Да
                                        </Button>
                                        <Button onClick={handleCloseDialog} disabled={deleting}>
                                            Не
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                            </AccordionActions>
                        </Accordion>
                    </Grid>
                ))}
            </Grid>
        </Container>
    )
}

export default ZoomPage
